import { createContext, useContext } from 'react';
import type { ComponentManifest, ThemeTokenSet, ViewResolution } from '@ayumia/contracts';
import type { RunSandbox, ServiceGateway } from '@ayumia/component-runtime';
import type { ApiClient } from '../api/client';
import type { Route } from '../app/router';

/** Ce que toute représentation de vue reçoit de l'application : jamais un accès à la base. */
export interface AppServices {
  client: ApiClient;
  gateway: ServiceGateway;
  /** `$context` des composants et des conditions de vue (ADR-048 §4) : rien de secret. */
  context: Readonly<Record<string, unknown>>;
  /** Portée `$theme` résolue. */
  theme: Readonly<Record<string, unknown>>;
  navigate: (route: Route) => void;
  /** Manifestes connus (livrés par `GetView`), par `artifact_id`. */
  components: ReadonlyMap<string, ComponentManifest>;
  /** Charge utile déclarée des commandes de la vue (`GetView`, ADR-064 §3), par nom. */
  commands?: ReadonlyMap<string, ViewCommand>;
  /**
   * Bac à sable de l'action `run` (ADR-083 §4), obtenu seulement par un composant qui en contient
   * une : l'interpréteur n'est chargé qu'à ce moment, dans un Web Worker.
   */
  runSandbox?: () => RunSandbox;
  /** `run_enabled` servi avec chaque composant qui contient `run` (paramètre `core.component_run`). */
  componentRun?: ReadonlyMap<string, boolean>;
  /**
   * Applique tout de suite une chaîne de thèmes relue par `GetExperience` (écran Thèmes, ADR-092) :
   * le choix se voit sans recharger l'application. Présentation seulement : le serveur a déjà décidé.
   */
  applyThemeChain?: (chain: readonly ThemeTokenSet[]) => void;
}

export type ViewCommand = NonNullable<ViewResolution['commands']>[number];

export const AppServicesContext = createContext<AppServices | null>(null);

export function useAppServices(): AppServices {
  const services = useContext(AppServicesContext);
  if (services === null) throw new Error('AppServicesContext absent.');
  return services;
}
